/**
 * Route Permission Utilities
 *
 * Utilities to get permission configuration for routes.
 */

import { Permission } from 'features/roles';
import { ROUTE_PERMISSIONS, RoutePermissionConfig } from 'config/permissions.config';

/**
 * Normalizes a route path by replacing dynamic segments with placeholders.
 * Example: '/projects/123' -> '/projects/:id'
 */
const normalizeRoutePath = (path: string): string => {
  // Remove trailing slashes
  let normalized = path.replace(/\/+$/, '');

  // Replace UUIDs and MongoDB ObjectIds with :id
  normalized = normalized.replace(/\/[a-f0-9]{24}(?=\/|$)/gi, '/:id');
  normalized = normalized.replace(/\/[a-f0-9-]{36}(?=\/|$)/gi, '/:id');

  // Replace numeric IDs with :id
  normalized = normalized.replace(/\/\d+(?=\/|$)/g, '/:id');

  return normalized;
};

/**
 * Gets the permission configuration for a given route path.
 *
 * @param path - The route path (e.g., '/projects', '/projects/123')
 * @returns The permission configuration or undefined if not found
 *
 * @example
 * const config = getRoutePermission('/projects');
 * // { permission: Permission.VIEW_PROJECTS }
 *
 * const config = getRoutePermission('/projects/123');
 * // { permission: Permission.VIEW_PROJECTS } (matches /projects/:id)
 */
export const getRoutePermission = (path: string): RoutePermissionConfig | undefined => {
  // First try exact match
  if (ROUTE_PERMISSIONS[path]) {
    return ROUTE_PERMISSIONS[path];
  }

  // Try normalized path
  const normalizedPath = normalizeRoutePath(path);
  if (ROUTE_PERMISSIONS[normalizedPath]) {
    return ROUTE_PERMISSIONS[normalizedPath];
  }

  // Try to find a matching pattern
  for (const [routePath, config] of Object.entries(ROUTE_PERMISSIONS)) {
    // Convert route pattern to regex
    const pattern = routePath
      .replace(/:[^/]+/g, '[^/]+') // Replace :param with regex
      .replace(/\//g, '\\/'); // Escape slashes

    const regex = new RegExp(`^${pattern}$`);

    if (regex.test(path) || regex.test(normalizedPath)) {
      return config;
    }
  }

  return undefined;
};

/**
 * Gets just the required permission(s) for a route.
 *
 * @param path - The route path
 * @returns The required permission(s) or undefined
 */
export const getRouteRequiredPermission = (
  path: string,
): Permission | Permission[] | undefined => {
  const config = getRoutePermission(path);
  return config?.permission;
};

/**
 * Checks if a route is admin-only.
 *
 * @param path - The route path
 * @returns True if the route requires admin access
 */
export const isRouteAdminOnly = (path: string): boolean => {
  const config = getRoutePermission(path);
  return config?.adminOnly === true;
};

/**
 * Gets the permission mode for a route (how to check multiple permissions).
 *
 * @param path - The route path
 * @returns 'ALL' (default) or 'ANY'
 */
export const getRoutePermissionMode = (path: string): 'ALL' | 'ANY' => {
  const config = getRoutePermission(path);
  return config?.permissionMode ?? 'ALL';
};

